import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DONUT_INITIAL_VALUES, Donut } from "@/pages/catalogs/models/Donut";
import { upsertDonut } from "@/pages/catalogs/repositories/DonutRepository";
import { CommentsField } from "@/shared/components/CommentsField";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DonutStatusSelector } from "./DonutStatusSelector";
import { LocationTreeSelector } from "./LocationTreeSelector";
import { SectionFamilySelector } from "./SectionFamilySelector";

type Props = {
	show: boolean;
	onHide: (partId?: number) => void;
};

export const NewDonutDialog = ({ show, onHide }: Props) => {
	const queryClient = useQueryClient();

	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const { control, reset, getValues, trigger } = useForm<Donut>({
		mode: "onChange",
		defaultValues: DONUT_INITIAL_VALUES,
	});

	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};

	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	const validateDiameters = (
		value: number | null,
		field: "Diameter" | "NewDiameter" | "ScrapDiameter",
	) => {
		const currentDiameter = getValues("Diameter") || 0;

		if (value === null || value === undefined) return true;

		switch (field) {
			case "NewDiameter":
				return (
					value >= currentDiameter ||
					"Must be greater than or equal to the current diameter"
				);
			case "ScrapDiameter":
				return (
					value < currentDiameter || "Must be less than the current diameter"
				);
			default:
				return true;
		}
	};

	const createDonut = useMutation({
		mutationFn: upsertDonut,
		onSuccess: async (partId) => {
			setNotificationMessage("Donut created successfully");
			await queryClient.invalidateQueries({ queryKey: ["donuts"] });
			onHide(partId);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const handleSave = async () => {
		const isValid = await trigger();
		if (!isValid) return;

		setIsSubmitLoading(true);
		const data = getValues();
		createDonut.mutate({ ...data, PartId: null });
	};

	const handleCancel = () => {
		reset(DONUT_INITIAL_VALUES);
		onHide();
	};

	const handleClose = () => {
		reset(DONUT_INITIAL_VALUES);
		onHide();
	};

	return (
		<>
			<MesfModal
				open={show}
				handleClose={handleClose}
				title="NEW DONUT"
				maxWidth="md"
				id="new-donut-modal"
			>
				<MesfModal.Content
					dividers
					sx={{
						padding: (theme) => theme.spacing(2),
						minWidth: 500,
						overflowY: "auto",
					}}
				>
					<Grid2 container spacing={1}>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="PartName"
								control={control}
								rules={{ required: "Name is required" }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										{...field}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-name-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="DonutStatusCode"
								rules={{ required: "Status is required" }}
								control={control}
								render={({ field, fieldState: { error } }) => (
									<DonutStatusSelector
										label="Status"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="SectionFamilyId"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<SectionFamilySelector
										label="Section Family"
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="Width"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Width"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-width-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="NewDiameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "NewDiameter"),
									},
									deps: ["Diameter", "ScrapDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Initial Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: { "data-test-id": "donut-new-diameter-input" },
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="Diameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "Diameter"),
									},
									deps: ["NewDiameter", "ScrapDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Current Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "donut-current-diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="ScrapDiameter"
								rules={{
									validate: {
										crossValidate: (value) =>
											validateDiameters(value, "ScrapDiameter"),
									},
									deps: ["Diameter", "NewDiameter"],
								}}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Scrap Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "donut-scrap-diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12 }}>
							<Controller
								name="Comments"
								control={control}
								render={({ field, fieldState: { error } }) => (
									<CommentsField
										value={field.value}
										onChange={field.onChange}
										fieldError={error}
										getValues={getValues}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>

				<MesfModal.Actions style={{ padding: "15px 30px" }}>
					<Grid2 container spacing={2} justifyContent="flex-end">
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="inherit"
								onClick={handleCancel}
								disabled={isSubmitLoading}
								fullWidth
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSave}
								startIcon={
									isSubmitLoading ? (
										<CircularProgress size="1rem" color="inherit" />
									) : null
								}
								disabled={isSubmitLoading}
								fullWidth
								data-test-id="create-donut-button"
							>
								Create
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Creating Donut"
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
